// src/pages/container_rental/components/timeline/TimelineToolbar.tsx
//
// 타임라인 조작대. 순수 프레젠테이션 — 상태는 RentalTimeline 이 들고 있다.
//
//  [월|분기|년] [−][+]   [◀][오늘][▶]   [연체만]
//  기간 from ~ to  + 프리셋 칩(3개월/6개월/1년/전체)
//  KPI 4종 (이번 달 발행 예정 / 완료 / 연체 / 만료임박 D-90)
//
// 구 매니페스트 잔재(먹 슬랩 라벨 · mono 대문자 코드 · radius 0 · 다크 KPI 띠)를
// 걷어내고 확정 디자인 시스템으로 맞췄다. 날짜·수치는 mono tabular 유지.

import { Box, TextField } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import { designTokens } from "../../../../theme.ts";
import type { ZoomLevel } from "./TimelineScale.ts";
import type { TimelineKpi } from "./timelineModel.ts";
import { EXPIRING_DAYS } from "./timelineModel.ts";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  caption: "#9AA0A6",
  neutral: "#F2F4F5",
  accentTint: "#FDEDE8",
  accentHover: "#C93A14",
  stopTint: "#FBEDED",
};

export type RangePreset = "3m" | "6m" | "1y" | "all";

const ZOOM_OPTIONS: Array<{ value: ZoomLevel; label: string }> = [
  { value: "month", label: "월" },
  { value: "quarter", label: "분기" },
  { value: "year", label: "년" },
];

const PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: "3m", label: "3개월" },
  { value: "6m", label: "6개월" },
  { value: "1y", label: "1년" },
  { value: "all", label: "전체" },
];

interface TimelineToolbarProps {
  zoom: ZoomLevel;
  onZoomChange: (z: ZoomLevel) => void;
  onZoomStep: (dir: -1 | 1) => void;
  onPan: (dir: -1 | 1) => void;
  onToday: () => void;
  from: string;
  to: string;
  onRangeChange: (from: string, to: string) => void;
  preset: RangePreset | "custom";
  onPresetChange: (p: RangePreset) => void;
  overdueOnly: boolean;
  onOverdueOnlyChange: (v: boolean) => void;
  kpi: TimelineKpi;
  /** 서버 range.today — "YYYY-MM-DD" */
  todayLabel: string;
  disabled?: boolean;
}

export default function TimelineToolbar({
  zoom,
  onZoomChange,
  onZoomStep,
  onPan,
  onToday,
  from,
  to,
  onRangeChange,
  preset,
  onPresetChange,
  overdueOnly,
  onOverdueOnlyChange,
  kpi,
  todayLabel,
  disabled,
}: TimelineToolbarProps) {
  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        bgcolor: C.surface,
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        overflow: "hidden",
        mb: 1.5,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {/* 1행 — 줌 / 이동 / 연체만 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          px: 1.5,
          py: 1.1,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Label>배율</Label>
        <SegGroup>
          {ZOOM_OPTIONS.map((o, i) => (
            <SegBtn
              key={o.value}
              active={zoom === o.value}
              divider={i > 0}
              onClick={() => onZoomChange(o.value)}
              title={`${o.label} 단위로 보기`}
            >
              {o.label}
            </SegBtn>
          ))}
        </SegGroup>

        <SegGroup>
          <IconBtn onClick={() => onZoomStep(-1)} title="축소">
            <RemoveIcon sx={{ fontSize: 15 }} />
          </IconBtn>
          <IconBtn onClick={() => onZoomStep(1)} title="확대" divider>
            <AddIcon sx={{ fontSize: 15 }} />
          </IconBtn>
        </SegGroup>

        <Box sx={{ flexGrow: 1 }} />

        <SegGroup>
          <IconBtn onClick={() => onPan(-1)} title="이전 화면">
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconBtn>
          <SegBtn onClick={onToday} divider title={`오늘 ${todayLabel}`}>
            오늘
          </SegBtn>
          <IconBtn onClick={() => onPan(1)} title="다음 화면" divider>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconBtn>
        </SegGroup>

        <Box
          role="button"
          tabIndex={0}
          onClick={() => onOverdueOnlyChange(!overdueOnly)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOverdueOnlyChange(!overdueOnly);
            }
          }}
          sx={{
            cursor: "pointer",
            userSelect: "none",
            outline: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.25,
            py: 0.6,
            borderRadius: "8px",
            border: `1px solid ${overdueOnly ? "#F0D6D6" : C.border}`,
            bgcolor: overdueOnly ? C.stopTint : C.surface,
            color: overdueOnly ? T.stop : C.ink,
            fontFamily: T.fonts.sans,
            fontSize: 13,
            fontWeight: overdueOnly ? 600 : 500,
            transition: "background-color 140ms ease, border-color 140ms ease, color 140ms ease",
            "&:hover": { bgcolor: overdueOnly ? C.stopTint : C.neutral },
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: T.stop,
              flexShrink: 0,
            }}
          />
          연체만
        </Box>
      </Box>

      {/* 2행 — 기간 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          px: 1.5,
          py: 1.1,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Label>기간</Label>
        <TextField
          type="date"
          size="small"
          value={from}
          onChange={(e) => onRangeChange(e.target.value, to)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={dateFieldSx}
          inputProps={{ "aria-label": "시작일" }}
        />
        <Box sx={{ fontFamily: T.fonts.sans, color: C.sub }}>~</Box>
        <TextField
          type="date"
          size="small"
          value={to}
          onChange={(e) => onRangeChange(from, e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={dateFieldSx}
          inputProps={{ "aria-label": "종료일" }}
        />

        <Box sx={{ display: "inline-flex", gap: 0.6, flexWrap: "wrap", ml: 0.5 }}>
          {PRESETS.map((p) => {
            const active = preset === p.value;
            return (
              <Box
                key={p.value}
                role="button"
                tabIndex={0}
                onClick={() => onPresetChange(p.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPresetChange(p.value);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  outline: "none",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "8px",
                  border: `1px solid ${active ? C.accentTint : C.border}`,
                  bgcolor: active ? C.accentTint : C.surface,
                  color: active ? C.accentHover : C.ink,
                  fontFamily: T.fonts.sans,
                  fontSize: 12.5,
                  fontWeight: active ? 600 : 500,
                  transition: "background-color 140ms ease, color 140ms ease",
                  "&:hover": { bgcolor: active ? C.accentTint : C.neutral },
                }}
              >
                {p.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* 3행 — KPI */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          bgcolor: C.field,
        }}
      >
        {/* PLAN 과 OVDU 는 모집단이 같다(진행중 계약). timelineModel.deriveKpi 주석 참조. */}
        <KpiCell
          label="이번 달 발행 예정"
          value={kpi.plannedThisMonth}
          accent={T.steel}
          title="진행중(active) 계약의 이번 달 발행 예정 회차 수"
        />
        <KpiCell
          label="이번 달 발행 완료"
          value={kpi.issuedThisMonth}
          accent={T.signal}
          title="계약 상태와 무관하게 이번 달에 발행된 회차 수"
        />
        <KpiCell
          label="연체"
          value={kpi.overdue}
          accent={T.stop}
          active={overdueOnly}
          title="진행중(active) 계약의 발행 예정일이 지난 회차 수"
          onClick={() => onOverdueOnlyChange(!overdueOnly)}
        />
        <KpiCell
          label={`만료임박 D-${EXPIRING_DAYS}`}
          value={kpi.expiring}
          accent={T.amber}
          last
        />
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */

const dateFieldSx = {
  width: 158,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: T.fonts.mono,
    fontSize: 12.5,
    bgcolor: C.field,
    fontVariantNumeric: "tabular-nums",
  },
  "& input": { py: 0.75 },
};

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: C.neutral,
        color: C.sub,
        px: 0.9,
        py: 0.3,
        borderRadius: "6px",
        fontFamily: T.fonts.sans,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

function SegGroup({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        border: `1px solid ${C.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: C.surface,
      }}
    >
      {children}
    </Box>
  );
}

function SegBtn({
  active,
  divider,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  divider?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      title={title}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        display: "inline-flex",
        alignItems: "center",
        px: 1.35,
        py: 0.6,
        bgcolor: active ? C.accentTint : C.surface,
        color: active ? C.accentHover : C.ink,
        borderLeft: divider ? `1px solid ${C.border}` : "none",
        fontFamily: T.fonts.sans,
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        whiteSpace: "nowrap",
        transition: "background-color 140ms ease, color 140ms ease",
        "&:hover": { bgcolor: active ? C.accentTint : C.neutral },
      }}
    >
      {children}
    </Box>
  );
}

function IconBtn({
  onClick,
  title,
  divider,
  children,
}: {
  onClick: () => void;
  title?: string;
  divider?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      title={title}
      aria-label={title}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        py: 0.5,
        bgcolor: C.surface,
        color: C.sub,
        borderLeft: divider ? `1px solid ${C.border}` : "none",
        transition: "background-color 140ms ease, color 140ms ease",
        "&:hover": { bgcolor: C.neutral, color: C.ink },
      }}
    >
      {children}
    </Box>
  );
}

function KpiCell({
  label,
  value,
  accent,
  last,
  active,
  title,
  onClick,
}: {
  label: string;
  value: number;
  accent: string;
  last?: boolean;
  active?: boolean;
  /** 모집단 설명 — 숫자끼리 모집단이 달라 보이는 오해를 막는다 */
  title?: string;
  onClick?: () => void;
}) {
  const clickable = Boolean(onClick);
  return (
    <Box
      title={title}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        px: { xs: 1.5, md: 2 },
        py: 1.25,
        borderTop: `1px solid ${C.border}`,
        borderRight: { sm: last ? "none" : `1px solid ${C.border}` },
        borderBottom: { xs: `1px solid ${C.border}`, sm: "none" },
        cursor: clickable ? "pointer" : "default",
        outline: "none",
        bgcolor: active ? C.stopTint : "transparent",
        transition: "background-color 140ms ease",
        "&:hover": clickable
          ? { bgcolor: active ? C.stopTint : C.neutral }
          : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontFamily: T.fonts.sans,
          fontSize: 12,
          fontWeight: 500,
          color: C.sub,
          mb: 0.4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: accent,
            flexShrink: 0,
          }}
        />
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: T.fonts.mono,
          fontWeight: 700,
          fontSize: { xs: 19, md: 22 },
          color: C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Number.isFinite(value) ? value : 0}
        <Box
          component="span"
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12,
            fontWeight: 500,
            ml: 0.5,
            color: C.caption,
          }}
        >
          건
        </Box>
      </Box>
    </Box>
  );
}
